import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, AudioLines, Loader2, Music, CheckCircle2, XCircle, Clock } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { SiteLogo } from "@/components/SiteLogo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SITE_NAME } from "@shared/site";

// Station presets: label + suggested prompt prefix
const STATION_PRESETS = [
  { id: "eclectic",     label: "The Pickle Jar — Eclectic",          genre: "eclectic mix" },
  { id: "rockthecourt", label: "Rock the Court — Classic Rock",       genre: "classic rock" },
  { id: "dinkandtwang", label: "Dink & Twang — Country",              genre: "country" },
  { id: "dinkdontstop", label: "Dink Don't Stop — Hip-Hop / R&B",     genre: "hip hop R&B" },
  { id: "popanddink",   label: "Pop & Dink — Pop / Indie",            genre: "indie pop" },
  { id: "smashanddrop", label: "Smash & Drop — Electronic / EDM",     genre: "electronic EDM" },
  { id: "graceanddink", label: "Grace & Dink — Christian",            genre: "contemporary Christian" },
];

interface GeneratedTask {
  taskId: string;
  prompt: string;
  station: string;
  startedAt: number;
}

function TaskCard({ task }: { task: GeneratedTask }) {
  const [enabled, setEnabled] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading } = trpc.music.pollTask.useQuery(
    { taskId: task.taskId },
    { enabled, refetchInterval: enabled ? 8000 : false }
  );

  useEffect(() => {
    if (data?.state === "succeeded" || data?.state === "failed") {
      setEnabled(false);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [data?.state]);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground font-mono truncate">{task.taskId}</p>
          <p className="text-sm font-medium mt-1 line-clamp-2">{task.prompt}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{task.station}</p>
        </div>
        <div className="shrink-0">
          {isLoading || (data?.state === "pending") ? (
            <Badge variant="secondary" className="gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Generating
            </Badge>
          ) : data?.state === "succeeded" ? (
            <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-3 h-3" /> Done
            </Badge>
          ) : data?.state === "failed" ? (
            <Badge variant="destructive" className="gap-1">
              <XCircle className="w-3 h-3" /> Failed
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <Clock className="w-3 h-3" /> Queued
            </Badge>
          )}
        </div>
      </div>

      {data?.state === "succeeded" && data.musicUrl && (
        <div className="pt-1">
          <audio controls className="w-full h-9" src={data.musicUrl} />
          <a
            href={data.musicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline underline-offset-2 mt-1 inline-block"
          >
            Download MP3
          </a>
        </div>
      )}
      {data?.state === "failed" && (
        <p className="text-xs text-destructive">{data.errorMessage ?? "Generation failed"}</p>
      )}
    </div>
  );
}

export default function Admin() {
  const { data: me, isLoading: meLoading } = trpc.session.me.useQuery();

  const [station, setStation] = useState(STATION_PRESETS[0].id);
  const [prompt, setPrompt] = useState("");
  const [tasks, setTasks] = useState<GeneratedTask[]>([]);

  useEffect(() => {
    document.title = `Admin — ${SITE_NAME}`;
  }, []);

  const generate = trpc.music.generate.useMutation({
    onSuccess: ({ taskId }) => {
      const preset = STATION_PRESETS.find(s => s.id === station) ?? STATION_PRESETS[0];
      setTasks(prev => [
        { taskId, prompt, station: preset.label, startedAt: Date.now() },
        ...prev,
      ]);
      setPrompt("");
    },
  });

  if (meLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me || me.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <XCircle className="w-12 h-12 text-destructive" />
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground">Admin access required.</p>
        <Link href="/">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Radio
          </Button>
        </Link>
      </div>
    );
  }

  const selectedPreset = STATION_PRESETS.find(s => s.id === station) ?? STATION_PRESETS[0];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border py-4 px-4 flex items-center gap-4">
        <Link href="/">
          <Button variant="ghost" size="icon" aria-label="Back to radio">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <SiteLogo className="h-8" />
        <div>
          <h1 className="font-bold leading-tight">{SITE_NAME} Admin</h1>
          <p className="text-xs text-muted-foreground">Music Generation</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Song audit entry */}
        <section>
          <Link href="/admin/songs">
            <Button variant="outline" className="gap-2 w-full justify-start h-12">
              <AudioLines className="w-5 h-5 text-primary" />
              <span className="flex flex-col items-start leading-tight">
                <span className="font-semibold">Song Audit</span>
                <span className="text-xs text-muted-foreground font-normal">
                  Listen to every song, read lyrics, flag &amp; remove tracks
                </span>
              </span>
            </Button>
          </Link>
        </section>

        {/* Generate form */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Music className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Generate a Song</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="station-select">Station</Label>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger id="station-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATION_PRESETS.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-input">
              Description Prompt
              <span className="ml-2 text-xs text-muted-foreground">
                (min 10 chars, describe style, mood, lyrics theme)
              </span>
            </Label>
            <Textarea
              id="prompt-input"
              placeholder={`e.g. upbeat ${selectedPreset.genre} song about pickleball tournament excitement, energetic and fun`}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {generate.error && (
            <p className="text-sm text-destructive">{generate.error.message}</p>
          )}

          <Button
            onClick={() => generate.mutate({ prompt })}
            disabled={prompt.trim().length < 10 || generate.isPending}
            className="gap-2"
          >
            {generate.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
            ) : (
              <><Music className="w-4 h-4" /> Generate Song</>
            )}
          </Button>
        </section>

        {/* Tasks list */}
        {tasks.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Generation Queue</h2>
            {tasks.map(t => (
              <TaskCard key={t.taskId} task={t} />
            ))}
          </section>
        )}
      </main>
    </div>
  );
}
